import _ from 'lodash';
import type qs from 'qs';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	DEBOUNCE_SEARCH_MS,
	useDebounceCallback,
	useHighlightMetaSafe,
	useRowsPerPage,
} from '@adflow/frontend/util-common';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import type { ParsedQ } from '../nav-action';
import { firstValue } from '../nav-action';

import {
	atPath,
	Lens,
	lensFrom,
	lensWithOnChange,
	parseIntLens,
	parseIntOrStrArrayLens,
	sortUniqLens,
	useUrlQueryLens,
} from './lens';
import type { NavigateOptions } from './use-navigate';

export const TABLE_ROWS_COUNT_UNKNOWN = Infinity;

const openRowsLens = lensFrom<qs.ParsedQs>()
	.then(atPath('openRows'))
	.then(parseIntOrStrArrayLens())
	.then(sortUniqLens());

function onPageChange(q: qs.ParsedQs) {
	// Scroll to top of the table when changing pages
	const table = document.querySelector('[role="table"]');
	if (table) {
		table.scrollTo({ top: 0, behavior: 'smooth' });
	}

	return openRowsLens.set([], q);
}

const currentPageLens = lensWithOnChange(
	lensFrom<qs.ParsedQs>()
		.then(atPath('page'))
		.then(parseIntLens())
		.then(
			// The page number is 1-indexed in the URL, but 0-indexed everywhere else.
			new Lens(
				(x) => (x ? Math.max(x - 1, 0) : 0),
				(x: number) => (x > 0 ? x + 1 : undefined)
			)
		),
	onPageChange
);

export function onResultSetChange(q: qs.ParsedQs) {
	return currentPageLens.set(0, onPageChange(q));
}

/**
 * NOTE: We used to run a side-effect onChange to clear out the
 * openRows, but this caused a bug where the openRows would be
 * cleared out after navigating from a notification link.
 */
export const rowsPerPageLens = lensFrom<qs.ParsedQs>()
	.then(atPath('rowsPerPage'))
	.then(parseIntLens())
	.then(
		new Lens(
			(x) => (x ? Math.min(Math.max(x, 1), 200) : null),
			(x: number | null | undefined) => (typeof x === 'number' ? x : undefined)
		)
	);

export interface SortParams {
	sort: string;
	sortDirection: 'asc' | 'desc';
}

const SORT = 'sort';
const SORT_DIRECTION = 'sortDirection';

export const sortLens = (defaultSort?: Partial<SortParams>) => {
	const defaultSortDirection = defaultSort?.sortDirection ?? 'asc';
	const reverseSortDirection = defaultSortDirection === 'asc' ? 'desc' : 'asc';
	return lensWithOnChange(
		new Lens<qs.ParsedQs, qs.ParsedQs, SortParams, Partial<SortParams>>(
			(queryParams: qs.ParsedQs) => {
				const querySortDirection = firstValue(
					_.get(queryParams, SORT_DIRECTION)
				);
				return {
					sort:
						firstValue(_.get(queryParams, SORT)) ??
						defaultSort?.sort ??
						'default',
					sortDirection:
						querySortDirection === undefined ||
						querySortDirection === defaultSortDirection
							? defaultSortDirection
							: reverseSortDirection,
				};
			},
			({ sort, sortDirection }: Partial<SortParams>, q: qs.ParsedQs) => {
				q = _.clone(q);
				_.set(q, SORT, sort && sort !== 'default' ? sort : undefined);
				_.set(q, SORT_DIRECTION, sortDirection);
				return q;
			}
		),
		onPageChange,
		(a, b) => a.sort === b.sort && a.sortDirection === b.sortDirection
	);
};

export function searchQueryLens(searchMinLength?: number) {
	return lensWithOnChange(
		lensFrom<qs.ParsedQs>()
			.then(atPath('searchQuery'))
			.then(
				new Lens(
					(x: ParsedQ) => firstValue(x) ?? '',
					(newSearchQuery: string, x: ParsedQ) =>
						newSearchQuery === ''
							? undefined
							: newSearchQuery.length >= (searchMinLength ?? 0)
								? newSearchQuery
								: x
				)
			),
		onResultSetChange
	);
}

interface UseTableParamsParams {
	itemType: DataTableItemType;
	defaultSort?: SortParams;
	minLength?: number;
	rowHeight?: number;
}

export function useTableParams({
	itemType,
	defaultSort,
	minLength = 0,
	rowHeight,
}: UseTableParamsParams) {
	const { t } = useTranslation('common');

	const [totalRows, setTotalRows] = useState(TABLE_ROWS_COUNT_UNKNOWN);
	const [searchInput, setSearchInput] = useState<null | string>(null);
	const [searchDebounce, cancelSearchDebounce, debounceRef] =
		useDebounceCallback();

	const highlight = useHighlightMetaSafe();

	const [openRows, setOpenRows] = useUrlQueryLens(openRowsLens, {
		replace: true,
	});
	const [currentPage, setCurrentPage] = useUrlQueryLens(currentPageLens);

	const { rowsPerPage, setRowsPerPage, tableWrapRef } = useRowsPerPage({
		itemType,
		rowHeight,

		useCalculatedRowsPerPageState: () =>
			useUrlQueryLens(rowsPerPageLens, { replace: true }),
	});

	const [sort, setSort] = useUrlQueryLens(sortLens(defaultSort));

	const [searchQuery, setSearchQueryLens] = useUrlQueryLens(
		useMemo(() => searchQueryLens(), [])
	);

	const setSearchQuery = useCallback(
		(value: string, navigateOptions?: NavigateOptions) => {
			// If the search query is less than the minimum length -> don't set it,
			// so we don't trigger a search.
			if (!(minLength && value.length < minLength && value.length > 0)) {
				setSearchQueryLens(value, navigateOptions);
			}

			if (value.length === 0) {
				setSearchLengthWarning(false);
			} else if (minLength) {
				setSearchLengthWarning(value.length < minLength);
			}
		},
		[minLength, setSearchQueryLens]
	);

	// two states for search: local and query
	// query is default and priority when changed
	// use local otherwise
	const setSearchDebounce = useCallback(
		(newSearchQuery: string, navigateOptions?: NavigateOptions) => {
			setSearchInput(newSearchQuery);
			searchDebounce(() => {
				setSearchQuery(newSearchQuery, navigateOptions);
			}, DEBOUNCE_SEARCH_MS);
		},
		[searchDebounce, setSearchQuery]
	);

	const setSearchImmediately = useCallback(
		(newSearchQuery: string) => {
			cancelSearchDebounce();
			setSearchQuery(newSearchQuery);
		},
		[cancelSearchDebounce, setSearchQuery]
	);

	const removeSearchTerm = useCallback(
		(text: string) => {
			const regex = new RegExp(
				`("|'|^|,| +)${_.escapeRegExp(text)}("|'|$|,| +)`,
				'igu'
			);

			const newSearchQuery =
				searchQuery?.replaceAll(regex, ' ').replaceAll(/\s\s+/gu, ' ').trim() ??
				'';
			setSearchImmediately(newSearchQuery);
		},
		[searchQuery, setSearchImmediately]
	);

	useEffect(() => {
		highlight?.setHighlightedText(searchQuery);

		return () => {
			// clear highlight on unmount
			highlight?.setHighlightedText('');
		};
	}, [highlight, searchQuery]);

	const useQueryTotalEffect = (totalItems: number | undefined) => {
		useEffect(() => {
			if (totalItems !== undefined) {
				setTotalRows(totalItems);
			}
		}, [totalItems]);
	};

	/**
	 * Update the searchTerm when the searchQuery is changed for reasons unrelated
	 * to user input (e.g. browser history change)
	 */
	useEffect(() => {
		const queryIsOutdated = !!debounceRef.current;
		if (searchInput !== searchQuery && !queryIsOutdated) {
			setSearchInput(searchQuery);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	// Ensure that page is within valid range of values
	useEffect(() => {
		if (rowsPerPage) {
			const lastPage = Math.floor(totalRows / rowsPerPage);
			if (currentPage && currentPage > lastPage && !searchQuery) {
				setCurrentPage(lastPage, { replace: true });
			}
		}
	}, [rowsPerPage, currentPage, setCurrentPage, totalRows, searchQuery]);

	const [searchLengthWarning, setSearchLengthWarning] = useState(false);

	const inputSearchProps = useMemo(
		() => ({
			value: searchInput ?? '',
			onChange: (e: ChangeEvent<HTMLInputElement>) => {
				setSearchDebounce(e.target.value);
			},
			onKeyUp: (e: KeyboardEvent<HTMLInputElement>) => {
				if (e.key === 'Enter') {
					setSearchImmediately((e.target as HTMLInputElement).value);
				}
			},
			handleClear: () => {
				setSearchImmediately('');
			},
			helperText: () =>
				searchLengthWarning ? t('searchMinLength', { minLength }) : undefined,
		}),
		[
			minLength,
			searchInput,
			searchLengthWarning,
			setSearchDebounce,
			setSearchImmediately,
			t,
		]
	);
	// TODO: This should use `satisfies InputSearchProps`, but this creates an import cycle.

	return {
		tableWrapRef,

		currentPage,
		setCurrentPage,
		rowsPerPage,
		setRowsPerPage,
		totalRows,
		setTotalRows,

		sort,
		setSort,

		openRows,
		setOpenRows,

		useQueryTotalEffect,

		/**
		 * The debounced search term. Use this as the input to your data query.
		 */
		searchQuery,

		/**
		 * The search term before debouncing. Use this as the value of the search
		 * input.
		 */
		searchInput,
		/**
		 * Use this to set the search term when it is typed by the user (when the
		 * search input is changed).
		 */
		setSearchInput: setSearchDebounce,
		/**
		 * Use this to set the search term immediately (for example, if the user
		 * presses Return after entering a search term).
		 */
		setSearchImmediately,
		/**
		 * Use this to remove a single segment from the search segment.
		 * Primarily used in products search.
		 */
		removeSearchTerm,
		/**
		 * If you are using an `<InputSearch>` element, spread these props onto it
		 * instead of directly using `searchInput`, `setSearchInput`, and
		 * `setSearchImmediately`.
		 */
		inputSearchProps,
	};
}
